import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./DiagnosticLogsPage.module.scss";

type DiagnosticPayload = {
  createdAt?: string;
  level?: string;
  method?: string;
  path?: string;
  pageUrl?: string;
  status?: number;
  durationMs?: number;
  response?: any;
  request?: any;
  error?: {
    message?: string;
    name?: string;
  };
  userAgent?: string;
};

type DiagnosticEntry = {
  lineNumber: number;
  nginxTime: string;
  payload: DiagnosticPayload;
  rawLine: string;
};

const extractPayload = (line: string): DiagnosticPayload | null => {
  const match = line.match(/payload=([^\s]+)\s+HTTP\//);
  if (!match) return null;

  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
};

const extractNginxTime = (line: string) => {
  return line.match(/\[([^\]]+)\]/)?.[1] ?? "";
};

const formatOmskTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Asia/Omsk",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const stringify = (value: any) => {
  if (!value) return "";
  return JSON.stringify(value, null, 2);
};

const getSummary = (payload: DiagnosticPayload) => {
  if (payload.error?.message) return `${payload.error.name ?? "Error"}: ${payload.error.message}`;
  if (payload.response?.errorMessage) return payload.response.errorMessage;
  if (payload.path === "/grz-input") {
    return `raw=${payload.response?.rawInput ?? ""} cleaned=${payload.response?.cleanedInput ?? ""} valid=${payload.response?.valid}`;
  }
  return payload.status ? `status ${payload.status}` : "diagnostic event";
};

const DiagnosticLogsPage = () => {
  const { zone } = useParams();
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key") ?? "";
  const [rawLog, setRawLog] = useState("");
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!key || zone !== "665") return;

    fetch(`/${zone}/logs?key=${encodeURIComponent(key)}`, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error(`Cannot load logs: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        setRawLog(text);
        setError("");
      })
      .catch((err) => {
        setError(err?.message ?? String(err));
      });
  }, [key, zone]);

  const entries = useMemo<DiagnosticEntry[]>(() => {
    return rawLog
      .split("\n")
      .map((line, index) => ({
        lineNumber: index + 1,
        nginxTime: extractNginxTime(line),
        payload: extractPayload(line),
        rawLine: line,
      }))
      .filter((entry): entry is DiagnosticEntry => !!entry.payload);
  }, [rawLog]);

  const filteredEntries = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return entries;

    return entries.filter((entry) => {
      return JSON.stringify(entry.payload).toLowerCase().includes(needle)
        || entry.nginxTime.toLowerCase().includes(needle);
    });
  }, [entries, filter]);

  if (zone !== "665") {
    return <div className={styles.page}>Logs view is available only for zone 665.</div>;
  }

  if (!key) {
    return <div className={styles.page}>Add key query param: /665/logs-view?key=...</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <div className={styles.kicker}>Zone 665 diagnostics</div>
          <h1>Frontend Logs</h1>
        </div>
        <button type="button" onClick={() => window.location.reload()}>Refresh</button>
      </div>

      <input
        className={styles.filter}
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder="Filter by GRZ, sessionID, paymentID, path, error..."
      />

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.meta}>
        Showing {filteredEntries.length} of {entries.length} events
      </div>

      <div className={styles.list}>
        {filteredEntries.map((entry) => (
          <div className={styles.card} key={`${entry.lineNumber}-${entry.payload.createdAt}-${entry.payload.path}`}>
            <div className={styles.cardHeader}>
              <div>
                <div className={styles.time}>{formatOmskTime(entry.payload.createdAt)} Omsk</div>
                <div className={styles.utc}>{entry.payload.createdAt ?? entry.nginxTime}</div>
              </div>
              <div className={`${styles.level} ${entry.payload.level === "error" ? styles.levelError : ""}`}>
                {entry.payload.level ?? "log"}
              </div>
            </div>

            <div className={styles.path}>{entry.payload.method} {entry.payload.path}</div>
            <div className={styles.summary}>{getSummary(entry.payload)}</div>

            <div className={styles.grid}>
              <div><span>Status</span>{entry.payload.status ?? "-"}</div>
              <div><span>Duration</span>{entry.payload.durationMs ?? "-"} ms</div>
              <div><span>Page</span>{entry.payload.pageUrl ?? "-"}</div>
              <div><span>User Agent</span>{entry.payload.userAgent ?? "-"}</div>
            </div>

            {entry.payload.response && (
              <details>
                <summary>Response / diagnostic data</summary>
                <pre>{stringify(entry.payload.response)}</pre>
              </details>
            )}

            {entry.payload.request && (
              <details>
                <summary>Request</summary>
                <pre>{stringify(entry.payload.request)}</pre>
              </details>
            )}

            <details>
              <summary>Raw line</summary>
              <pre>{entry.rawLine}</pre>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DiagnosticLogsPage;
